/**
 * One supplier <-> customer message thread: the messages and the box to reply.
 *
 * CR-8605: shared by the customer's Communication Log panel
 * (pages/supplier-detail/ChatPanel) and the supplier's Messages window
 * (pages/CustomerChatDialog), which had drifted into two copies. A failed load
 * now says so with a Retry instead of showing "No messages yet", and a refused
 * send shows the server's reason instead of a generic failure.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { suppliersAPI, type SupplierMessage } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { AlertCircle, Loader2, RefreshCw, Send, User } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface SupplierMessageThreadProps {
  supplierId: number;
  customerId: number;
  /** Called once the thread has loaded — opening it marks the other side's messages read. */
  onRead?: () => void;
  /** Sizing for the scrolling message list. */
  listClassName?: string;
}

export default function SupplierMessageThread({
  supplierId, customerId, onRead, listClassName = 'min-h-[300px] max-h-[500px]',
}: SupplierMessageThreadProps) {
  const { user } = useAuth();
  const { formatDateTime } = useFormatters();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;

  const [messages, setMessages] = useState<SupplierMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // Switching thread mid-request must not show the previous thread's reply.
    let current = true;
    setLoading(true);
    setLoadError(null);
    suppliersAPI.getMessages({ supplier: supplierId, customer: customerId, page_size: 100 })
      .then((res) => {
        if (!current) return;
        setMessages(res.data.results || []);
        onReadRef.current?.();
      })
      .catch((err: unknown) => {
        if (!current) return;
        console.error('Failed to fetch messages:', err);
        setMessages([]);
        setLoadError(getApiErrorMessage(err, 'Messages could not be loaded.'));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => { current = false; };
  }, [supplierId, customerId, reloadKey]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(async () => {
    const content = newMessage.trim();
    if (!content || sending) return;
    try {
      setSending(true);
      const res = await suppliersAPI.sendMessage({ supplier: supplierId, customer: customerId, content });
      setMessages((prev) => [...prev, res.data]);
      setNewMessage('');
    } catch (err: unknown) {
      console.error('Failed to send message:', err);
      // The typed text stays in the box so nothing is lost.
      toast.error(getApiErrorMessage(err, 'Message not sent. Please try again.'));
    } finally {
      setSending(false);
    }
  }, [newMessage, sending, supplierId, customerId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      );
    }
    if (loadError) {
      return (
        <div className="flex flex-col items-center gap-3 py-8 text-center text-sm">
          <p className="flex items-center gap-1.5 text-destructive">
            <AlertCircle className="h-4 w-4" />
            {loadError}
          </p>
          <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Retry
          </Button>
        </div>
      );
    }
    if (messages.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground text-sm">
          <p>No messages yet. Start the conversation.</p>
        </div>
      );
    }
    return messages.map((msg) => {
      const isMe = msg.sender === user?.id;
      return (
        <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
          <div className="flex items-center gap-1.5 mb-0.5">
            {!isMe && <User className="h-3 w-3 text-muted-foreground" />}
            <span className="text-xs font-medium">{isMe ? 'You' : msg.sender_name}</span>
            <span className="text-[10px] text-muted-foreground">{formatDateTime(msg.created_at)}</span>
          </div>
          <div
            className={`rounded-lg px-3 py-2 max-w-[85%] text-sm whitespace-pre-wrap break-words ${
              isMe ? 'bg-primary text-primary-foreground' : 'bg-muted'
            }`}
          >
            {msg.content}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className={`flex-1 overflow-y-auto py-3 space-y-3 ${listClassName}`}>
        {renderBody()}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t pt-3">
        <Textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={2}
          maxLength={2000}
          disabled={!!loadError}
          className="resize-none min-h-0"
        />
        <div className="flex justify-end mt-2">
          <Button size="sm" disabled={!newMessage.trim() || sending || !!loadError} onClick={handleSend}>
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
